import React, { useState } from "react";
import { useUser } from "../../../context/UserContext";
import { canAccessAdmin } from "../../../utils/roleUtils";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "../../../utils/toast";
import { sanitizeText, sanitizeEmail, sanitizePhone } from "../../../utils/sanitization";
import BaseModal from "../../ui/BaseModal";
import "../../../styles/modern-design-system.css";

// Cross-browser compatibility polyfills
const safeTrim = (str) => {
  if (typeof str !== 'string') return '';
  return str.trim ? str.trim() : str.replace(/^\s+|\s+$/g, '');
};

const safeToLowerCase = (str) => {
  if (typeof str !== 'string') return '';
  return str.toLowerCase ? str.toLowerCase() : str.replace(/[A-Z]/g, function(match) {
    return String.fromCharCode(match.charCodeAt(0) + 32);
  });
};

const CreateLeadModal = ({ isOpen, onClose, onLeadCreated }) => {
  const { user } = useUser();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [destination, setDestination] = useState("disney_world");
  const [departureDate, setDepartureDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [source, setSource] = useState("website");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Access control for premium destinations
  const hasNewDestinationAccess = () => {
    return user && canAccessAdmin(user.role);
  };

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (phoneNumber.length >= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    } else if (phoneNumber.length >= 3) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumber.length > 0) {
      return `(${phoneNumber}`;
    }
    return phoneNumber;
  };

  // Auto-capitalize function for name fields
  const capitalizeName = (value) => {
    return value
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: undefined }));
    }
  };

  if (!isOpen) return null;

  // Validation functions - cross-browser compatible
  const validateEmail = (email) => {
    if (!email || typeof email !== 'string') return false;
    
    const trimmedEmail = safeTrim(email);
    if (trimmedEmail.length === 0) return false;
    
    // More comprehensive email validation regex
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    
    // Additional checks
    if (!emailRegex.test(trimmedEmail)) return false;
    if (trimmedEmail.length > 254) return false; // RFC 5321 limit
    if (trimmedEmail.includes('..')) return false; // No consecutive dots
    if (trimmedEmail.startsWith('.') || trimmedEmail.endsWith('.')) return false; // No leading/trailing dots
    
    return true;
  };

  const validatePhone = (phone) => {
    if (!phone || !safeTrim(phone)) return true; // Phone is optional
    // Simplified phone regex for better browser compatibility
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{7,20}$/;
    return phoneRegex.test(phone);
  };

  const validateName = (name) => {
    if (!name || typeof name !== 'string') return false;
    const trimmedName = safeTrim(name);
    if (trimmedName.length < 2) return false;
    if (trimmedName.length > 50) return false;
    return true;
  };

  const validateDate = (dateString) => {
    if (!dateString) return true; // Dates are optional
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  };

  const validateAdults = (adults) => {
    const num = parseInt(adults);
    return num > 0 && num <= 20;
  };

  const validateChildren = (children) => {
    const num = parseInt(children);
    return num >= 0 && num <= 20;
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!validateName(firstName)) {
      newErrors.firstName = "First name is required (2-50 characters)";
    }
    if (!validateName(lastName)) {
      newErrors.lastName = "Last name is required (2-50 characters)";
    }
    if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Optional fields with validation
    if (phone && !validatePhone(phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }
    if (!validateAdults(adults)) {
      newErrors.adults = "Adults must be between 1 and 20";
    }
    if (!validateChildren(children)) {
      newErrors.children = "Children must be between 0 and 20";
    }
    if (departureDate && !validateDate(departureDate)) {
      newErrors.departureDate = "Please enter a valid departure date";
    }
    if (returnDate && !validateDate(returnDate)) {
      newErrors.returnDate = "Please enter a valid return date";
    }

    // Date logic validation
    if (departureDate && returnDate) {
      const depDate = new Date(departureDate);
      const retDate = new Date(returnDate);
      if (retDate <= depDate) {
        newErrors.returnDate = "Return date must be after departure date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/crm/leads`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstname: safeTrim(sanitizeText(capitalizeName(firstName))),
          lastname: safeTrim(sanitizeText(capitalizeName(lastName))),
          email: safeTrim(safeToLowerCase(sanitizeEmail(email))),
          phone: safeTrim(sanitizePhone(phone)) || null,
          adults: parseInt(adults) || 2,
          children: parseInt(children) || 0,
          destination: destination,
          departure_date: departureDate || null,
          return_date: returnDate || null,
          source: source,
          message: safeTrim(sanitizeText(message)) || null,
          status: 'new'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create lead');
      }

      const result = await response.json();
      
      toast.success("Lead created successfully!");
      
      if (onLeadCreated) {
        onLeadCreated(result.data);
      }
      
      // Reset form
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setAdults(2);
      setChildren(0);
      setDestination("disney_world");
      setDepartureDate("");
      setReturnDate("");
      setSource("website");
      setMessage("");
      setErrors({});
      
      onClose();
    } catch (error) {
      console.error('Error creating lead:', error);
      toast.error(error.message || 'Failed to create lead');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form when closing
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setAdults(2);
      setChildren(0);
      setDestination("disney_world");
      setDepartureDate("");
      setReturnDate("");
      setSource("website");
      setMessage("");
      setErrors({});
      onClose();
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={handleClose} size="lg" showClose={false}>
      <div className="p-5 overflow-y-auto max-h-[75vh]">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="mb-1 text-2xl font-bold text-gray-900">
              Create New Lead
            </h2>
            <p className="text-sm text-gray-500">
              Enter lead information to add them to your CRM system
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Personal Information */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label htmlFor="firstName" className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                First Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(capitalizeName(sanitizeText(e.target.value)));
                    if (errors.firstName) {
                      setErrors(prev => ({ ...prev, firstName: undefined }));
                    }
                  }}
                  required
                  placeholder="Enter first name"
                  disabled={loading}
                  maxLength={50}
                  className={`block w-full px-3 py-2 text-sm font-medium bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 placeholder-gray-300 appearance-none ${
                    errors.firstName ? 'border-red-500 focus:ring-red-500' : 
                    firstName ? 'border-blue-300 focus:ring-blue-500' : ''
                  }`}
                  style={{
                    fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    height: '36px'
                  }}
                  aria-describedby={`${errors.firstName ? 'firstName-help' : ''}`}
                  aria-invalid={errors.firstName ? 'true' : 'false'}
                  autoComplete="off"
                />
                
                {/* Validation Icons */}
                {errors.firstName && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  </div>
                )}
                {firstName && !errors.firstName && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <CheckCircle className="w-5 h-5 text-blue-500" />
                  </div>
                )}
              </div>
              
              {errors.firstName && (
                <p id="firstName-help" className="flex items-center space-x-1 text-sm text-red-400" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.firstName}</span>
                </p>
              )}
            </div>

            <div>
              <label htmlFor="lastName" className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                Last Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(capitalizeName(sanitizeText(e.target.value)));
                    if (errors.lastName) {
                      setErrors(prev => ({ ...prev, lastName: undefined }));
                    }
                  }}
                  required
                  placeholder="Enter last name"
                  disabled={loading}
                  maxLength={50}
                  className={`block w-full px-3 py-2 text-sm font-medium bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 placeholder-gray-300 appearance-none ${
                    errors.lastName ? 'border-red-500 focus:ring-red-500' : 
                    lastName ? 'border-blue-300 focus:ring-blue-500' : ''
                  }`}
                  style={{
                    fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    height: '36px'
                  }}
                  aria-describedby={`${errors.lastName ? 'lastName-help' : ''}`}
                  aria-invalid={errors.lastName ? 'true' : 'false'}
                  autoComplete="off"
                />
                
                {/* Validation Icons */}
                {errors.lastName && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  </div>
                )}
                {lastName && !errors.lastName && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <CheckCircle className="w-5 h-5 text-blue-500" />
                  </div>
                )}
              </div>
              
              {errors.lastName && (
                <p id="lastName-help" className="flex items-center space-x-1 text-sm text-red-400" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.lastName}</span>
                </p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              Email Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  const lowercasedEmail = sanitizeEmail(e.target.value.toLowerCase());
                  setEmail(lowercasedEmail);
                  if (errors.email) {
                    setErrors(prev => ({ ...prev, email: undefined }));
                  }
                }}
                required
                placeholder="Enter email address"
                disabled={loading}
                className={`block w-full px-3 py-2 text-sm font-medium bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 placeholder-gray-300 appearance-none ${
                  errors.email ? 'border-red-500 focus:ring-red-500' : 
                  email ? 'border-blue-300 focus:ring-blue-500' : ''
                }`}
                style={{
                  fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  height: '36px'
                }}
                aria-describedby={`${errors.email ? 'email-help' : ''}`}
                aria-invalid={errors.email ? 'true' : 'false'}
                autoComplete="off"
              />
              
              {/* Validation Icons */}
              {errors.email && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>
              )}
              {email && !errors.email && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                </div>
              )}
            </div>
            
            {errors.email && (
              <p id="email-help" className="flex items-center space-x-1 text-sm text-red-400" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                <AlertCircle className="w-4 h-4" />
                <span>{errors.email}</span>
              </p>
            )}
          </div>

          <div>
            <label htmlFor="phone" className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              Phone Number (Optional)
            </label>
            <div className="relative">
              <input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value);
                  setPhone(formatted);
                  if (errors.phone) {
                    setErrors(prev => ({ ...prev, phone: undefined }));
                  }
                }}
                placeholder="(###) ###-####"
                disabled={loading}
                maxLength={14}
                className={`block w-full px-3 py-2 text-sm font-medium bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 placeholder-gray-300 appearance-none ${
                  errors.phone ? 'border-red-500 focus:ring-red-500' : 
                  phone ? 'border-blue-300 focus:ring-blue-500' : ''
                }`}
                style={{
                  fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  height: '36px'
                }}
                aria-describedby={`${errors.phone ? 'phone-help' : ''}`}
                aria-invalid={errors.phone ? 'true' : 'false'}
                autoComplete="off"
              />
              
              {/* Validation Icons */}
              {errors.phone && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>
              )}
              {phone && !errors.phone && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                </div>
              )}
            </div>
            
            {errors.phone && (
              <p id="phone-help" className="flex items-center space-x-1 text-sm text-red-400" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                <AlertCircle className="w-4 h-4" />
                <span>{errors.phone}</span>
              </p>
            )}
          </div>

          {/* Trip Information */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <div className="sm:col-span-2">
              <label htmlFor="destination" className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                Destination
              </label>
              <div className="relative">
                <select
                  id="destination"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="px-3 py-2 w-full text-sm font-medium text-white bg-gray-700 rounded-lg border border-gray-600 transition-all duration-200 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
                  style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif', height: '36px' }}
                  disabled={loading}
                >
                  <option value="disney_world">Disney World</option>
                  <option value="disneyland" disabled={!hasNewDestinationAccess()}>Disneyland</option>
                  <option value="universal_orlando" disabled={!hasNewDestinationAccess()}>Universal Orlando</option>
                  {hasNewDestinationAccess() && (
                    <>
                      <option value="disney_cruise">Disney Cruise Line</option>
                      <option value="universal_hollywood">Universal Hollywood</option>
                    </>
                  )}
                </select>
                <svg
                  className="absolute right-3 top-1/2 w-5 h-5 text-gray-300 -translate-y-1/2 pointer-events-none"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.17l3.71-2.94a.75.75 0 111.04 1.08l-4.24 3.36a.75.75 0 01-.94 0L5.21 8.31a.75.75 0 01.02-1.1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            <div>
              <label htmlFor="adults" className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                Adults
              </label>
              <input
                id="adults"
                type="number"
                value={adults}
                onChange={(e) => {
                  setAdults(parseInt(e.target.value) || 1);
                  if (errors.adults) {
                    setErrors(prev => ({ ...prev, adults: undefined }));
                  }
                }}
                min="1"
                max="20"
                className={`px-3 py-2 w-full font-medium text-white bg-gray-700 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-gray-300 ${
                  errors.adults ? 'border-red-500 focus:ring-red-500' : 'border-gray-600'
                }`}
                style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif', height: '36px' }}
                disabled={loading}
              />
              {errors.adults && (
                <p className="mt-1 text-sm text-red-600">{errors.adults}</p>
              )}
            </div>

            <div>
              <label htmlFor="children" className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                Children
              </label>
              <input
                id="children"
                type="number"
                value={children}
                onChange={(e) => {
                  setChildren(parseInt(e.target.value) || 0);
                  if (errors.children) {
                    setErrors(prev => ({ ...prev, children: undefined }));
                  }
                }}
                min="0"
                max="20"
                className={`px-3 py-2 w-full font-medium text-white bg-gray-700 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-gray-300 ${
                  errors.children ? 'border-red-500 focus:ring-red-500' : 'border-gray-600'
                }`}
                style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif', height: '36px' }}
                disabled={loading}
              />
              {errors.children && (
                <p className="mt-1 text-sm text-red-600">{errors.children}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="departureDate" className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                Departure Date
              </label>
              <input
                id="departureDate"
                type="date"
                value={departureDate}
                onChange={(e) => {
                  setDepartureDate(e.target.value);
                  if (errors.departureDate) {
                    setErrors(prev => ({ ...prev, departureDate: undefined }));
                  }
                }}
                className={`px-3 py-2 w-full font-medium text-white bg-gray-700 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-gray-300 ${
                  errors.departureDate ? 'border-red-500 focus:ring-red-500' : 'border-gray-600'
                }`}
                style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif', height: '36px' }}
                disabled={loading}
              />
              {errors.departureDate && (
                <p className="mt-1 text-sm text-red-600">{errors.departureDate}</p>
              )}
            </div>

            <div>
              <label htmlFor="returnDate" className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                Return Date
              </label>
              <input
                id="returnDate"
                type="date"
                value={returnDate}
                onChange={(e) => {
                  setReturnDate(e.target.value);
                  if (errors.returnDate) {
                    setErrors(prev => ({ ...prev, returnDate: undefined }));
                  }
                }}
                className={`px-3 py-2 w-full font-medium text-white bg-gray-700 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-gray-300 ${
                  errors.returnDate ? 'border-red-500 focus:ring-red-500' : 'border-gray-600'
                }`}
                style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif', height: '36px' }}
                disabled={loading}
              />
              {errors.returnDate && (
                <p className="mt-1 text-sm text-red-600">{errors.returnDate}</p>
              )}
            </div>
          </div>

          {/* Lead Source */}
          <div>
            <label htmlFor="source" className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              Lead Source
            </label>
            <div className="relative">
              <select
                id="source"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="px-3 py-2 w-full text-sm font-medium text-white bg-gray-700 rounded-lg border border-gray-600 transition-all duration-200 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
                style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif', height: '36px' }}
                disabled={loading}
              >
                <option value="website">Website</option>
                <option value="widget">Website Widget</option>
                <option value="referral">Referral</option>
                <option value="social">Social Media</option>
                <option value="other">Other</option>
              </select>
              <svg
                className="absolute right-3 top-1/2 w-5 h-5 text-gray-300 -translate-y-1/2 pointer-events-none"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.17l3.71-2.94a.75.75 0 111.04 1.08l-4.24 3.36a.75.75 0 01-.94 0L5.21 8.31a.75.75 0 01.02-1.1z" clipRule="evenodd" />
              </svg>
            </div>
          </div>

          <div>
            <label htmlFor="message" className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              Notes/Message
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              className="px-3 py-2 w-full font-medium text-white bg-gray-700 rounded-lg border border-gray-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-gray-300 resize-vertical"
              style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}
              placeholder="Any additional notes about this lead..."
              disabled={loading}
            />
          </div>

          <div className="flex gap-3 pt-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? (
                <div className="flex justify-center items-center">
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Creating...
                </div>
              ) : (
                "Create Lead"
              )}
            </button>
          </div>
        </form>
      </div>
    </BaseModal>
  );
};

export default CreateLeadModal;
